import Utils2D from '../../utils/utils2D.js';
import Utils3D from '../utils/utils3D.js';
import GeoUtils from './geoUtils.js';
import CoreUtils from '../../utils/coreUtils.js';

export const POINT_TYPE = 
{
	LINEAR:		0,
	BEZIER:		1
}

const SPLINE_EPSILON = 1e-10;
const COINCIDENT_EPSILON = 1e-10;
const COLLINEAR_EPSILON = 1e-5;
// Interactive tolerance
//const COLLINEAR_EPSILON = 0.001;
//const RESAMPLE_ERROR = 0.0025;
const DEFAULT_RESAMPLE_ERROR = 0.001;

export const RESAMPLE_LAST_POINT_OPTION =
{
	DELETE:			0,
	ADD_ORIGINAL:	1,
	EXTEND:			2,
};

export default class Spline
{
	constructor(pointData = null, bIsClosed = false)
	{
		this.points = [];
		this.isClosed = bIsClosed;
		this.cachedBBox = null;
		this.cachedLinearPoints = null;
		this.cachedLinearPointParams = {};
		if(pointData)
		{
			if(pointData.points !== undefined)
				this.setFromPointList(pointData.points, pointData.isClosed);
			else
				this.setFromLinearPoints(pointData, bIsClosed);
		}			
	}

	resetCaches()
	{
		this.cachedBBox = null;
		this.cachedLinearPoints = null;
		this.cachedLinearPointParams = {};
	}

	allocateNewPoint3D(x = 0, y = 0, z = 0)
	{
		return new BABYLON.Vector3(x, y, z);
	}

	allocateNewPoint2D(x = 0, y = 0)
	{
		return new BABYLON.Vector2(x, y);
	}

	allocateNewPoint(x = 0, y = 0, z = 0)
	{
		if(this.getIs3D())
			return this.allocateNewPoint3D(x, y, z);
		else
			return this.allocateNewPoint2D(x, y);
	}

	static getExtraData(obj)
	{
		let resData = {};
		if(!obj)
			return resData;

		const keys = Object.keys(obj);
		for(let currKey of keys)
		{
			if(currKey !== 'point' && currKey !== 'type')
				resData[currKey] = obj[currKey];
		}
		return resData;
	}

	setExtraData(iPointIndex, newData)
	{
		if(!this.points[iPointIndex])
			return;

		this.points[iPointIndex] = {
			point: this.points[iPointIndex].point,
			type: this.points[iPointIndex].type,
			...newData
		};
	}

	getIsEqual(otherSpline)
	{
		if(!otherSpline)
			return false;

		if(otherSpline.isClosed !== this.isClosed)
			return false;

		if(this.points.length !== otherSpline.points.length)
			return false;

		const is3D = this.getIs3D();
		if(otherSpline.getIs3D() !== is3D)
			return;

		let distFunc = is3D ? BABYLON.Vector3.DistanceSquared : BABYLON.Vector2.DistanceSquared;

		// Otherwise, go through each point and compare distance and types:
		for(let iPoint = 0; iPoint < this.points.length; iPoint++)
		{
			const pointInfo1 = this.points[iPoint];
			const pointInfo2 = otherSpline.points[iPoint];
			if(pointInfo2.type !== pointInfo1.type)
				return false;

			if(distFunc(pointInfo1.point, pointInfo2.point) >= 1e-12)
				return false;
		}

		return true;
	}

	setIsClosed(bValue)
	{
		this.isClosed = bValue;
		this.resetCaches();
	}

	getIsClosed()
	{
		return this.isClosed;
	}

	getNumPoints()
	{
		return this.points.length;
	}

	saveToObject()
	{
		return {
			points: this.points.map((pointData) => {
				return { 
					type: pointData.type, 
					point: 
					{
						x: pointData.point.x,
						y: pointData.point.y,
						z: pointData.point.z
					},
					...Spline.getExtraData(pointData)
				}
			}),
			isClosed: this.isClosed,
		};
	}

	clone()
	{
		let pCopy = this.saveToObject();
		let pNewSpline = new Spline();
		pNewSpline.loadFromObject(pCopy);
		return pNewSpline;
	}

	loadFromObject(pObj)
	{
		this.setFromPointList(pObj['points'], pObj['isClosed']);
		this.isClosed = pObj['isClosed'];
		this.resetCaches();
	}

	getIs3D()
	{
		if(this.points.length > 0 && this.points[0].point.z !== undefined)
			return true;
		return false;
	}

	getPointIndex(point) {
		return this.points.findIndex((potentialPoint) => {
			return potentialPoint.point.equalsWithEpsilon(point, COINCIDENT_EPSILON);
		});
	}

	setFromLinearPoints(vecPoints, bIsClosed)
	{
		this.points = [];
		this.isClosed = !!bIsClosed;

		if(!vecPoints || !vecPoints.forEach)
			return;

		vecPoints.forEach((vecPoint) =>
		{
			if(vecPoint.z !== undefined)
				this.points.push({point: this.allocateNewPoint3D(vecPoint.x, vecPoint.y, vecPoint.z), type: POINT_TYPE.LINEAR});
			else
				this.points.push({point: this.allocateNewPoint2D(vecPoint.x, vecPoint.y), type: POINT_TYPE.LINEAR});
		});

		// Auto-detect cases where the spline is closed if the first and last line are together:
		this.autodetectClosedStatus();
		this.resetCaches();
	}

	autodetectClosedStatus(epsilon = COINCIDENT_EPSILON)
	{
		if(this.points.length <= 1)
			return;

		let fEndsDistance = this.points[0].point.subtract(this.points[this.points.length - 1].point).length();
		if(fEndsDistance < epsilon)
		{
			this.points.pop();
			this.isClosed = true;
		}
	}

	setFromPointList(pointList, bIsClosed)
	{
		this.points = [];
		this.isClosed = false;
		if(!pointList || !pointList.forEach)
			return;
		
		this.isClosed = !!bIsClosed;
		pointList.forEach((pointInfo) =>
		{
			let vecPoint = pointInfo.point;
			let eType = pointInfo.type;
			if(!vecPoint)
			{
				vecPoint = pointInfo;
				eType = POINT_TYPE.LINEAR;
			}

			if(vecPoint.x === null) {
				console.warn(`Nulls detected in spline point data!`);
				return;
			}

			const extraData = Spline.getExtraData(pointInfo);
			if(vecPoint.z !== undefined)
				this.points.push({point: this.allocateNewPoint3D(vecPoint.x, vecPoint.y, vecPoint.z), type: eType, ...extraData });
			else
				this.points.push({point: this.allocateNewPoint2D(vecPoint.x, vecPoint.y), type: eType, ...extraData });
		});
		this.resetCaches();
	}

	insertPoint(iIndex, vecPoint, eType = POINT_TYPE.LINEAR)
	{
		this.points.splice(iIndex, 0, { point: vecPoint.clone(), type: eType });
		this.resetCaches();
	}

	toPointList()
	{
		return this.points;
	}

	getPoint(iIndex)
	{
		return this.points[iIndex];
	}

	setPointPosition(iIndex, fX, fY)
	{
		this.points[iIndex].point.x = fX;
		this.points[iIndex].point.y = fY;
		this.resetCaches();
	}

	addLinearPoint(fX, fY)
	{
		this.points.push({point: this.allocateNewPoint2D(fX, fY), type: POINT_TYPE.LINEAR});
		this.resetCaches();
	}

	addBezierSegment(fControl1X, fControl1Y, fControl2X, fControl2Y, fEndX, fEndY)
	{
		this.points.push({point: this.allocateNewPoint2D(fControl1X, fControl1Y), type: POINT_TYPE.BEZIER});
		this.points.push({point: this.allocateNewPoint2D(fControl2X, fControl2Y), type: POINT_TYPE.BEZIER});
		this.points.push({point: this.allocateNewPoint2D(fEndX, fEndY), type: POINT_TYPE.LINEAR});
		this.resetCaches();
	}

	convertBezierRecursive(vecStart, vecControl1, vecControl2, vecEnd, vecLinStart, vecLinEnd, tStart, tEnd, fResampleError, vecConvPoints)
	{
		let tMid = (tStart + tEnd)/2.0;
		let vecLinearMid = vecLinEnd.add(vecLinStart).scale(0.5);

		let vecBezierPos = vecStart.scale(Math.pow(1 - tMid, 3))
			.add(vecControl1.scale(Math.pow(1 - tMid, 2)*3*tMid))
			.add(vecControl2.scale((1 - tMid)*tMid*tMid*3))
			.add(vecEnd.scale(tMid*tMid*tMid));

		let fError = vecBezierPos.subtract(vecLinearMid).length();
		if(fError < fResampleError)
			return;

		this.convertBezierRecursive(vecStart, vecControl1, vecControl2, vecEnd, vecLinStart, vecBezierPos, tStart, tMid, fResampleError, vecConvPoints);
		vecConvPoints.push(vecBezierPos);
		this.convertBezierRecursive(vecStart, vecControl1, vecControl2, vecEnd, vecBezierPos, vecLinEnd, tMid, tEnd, fResampleError, vecConvPoints);
	}

	convertBezierToLinear(vecStart, vecControl1, vecControl2, vecEnd, fResampleError = DEFAULT_RESAMPLE_ERROR)
	{
		let vecConvPoints = [vecStart];
		this.convertBezierRecursive(vecStart, vecControl1, vecControl2, vecEnd, vecStart, vecEnd, 0.0, 1.0, fResampleError, vecConvPoints);
		vecConvPoints.push(vecEnd);
		return vecConvPoints;
	}

	ensureFullyLinear(fResampleError = DEFAULT_RESAMPLE_ERROR)
	{
		if(this.getIsFullyLinear())
			return;

		// Otherwise, we have beziers. Resample them:
		let convertedPoints;
		if(this.getIs3D())
			convertedPoints = this.getLinearPoints3D();
		else
			convertedPoints = this.getLinearPoints();

		this.loadFromObject({points: convertedPoints, isClosed: this.isClosed});
	}

	getIsFullyLinear()
	{
		for(let index = 0; index < this.points.length; index++)
		{
			if(this.points[index].type === POINT_TYPE.BEZIER)
				return false;
		}

		return true;
	}

	removeCollinearPoints(collinearEpsilon = COLLINEAR_EPSILON)
	{
		if(this.points.length <= 2)
			return;

		let iEndValue = 0;
		if(!this.getIsClosed())
			iEndValue = 2;

		const bIs3D = this.getIs3D();
		
		let fDist;
		let vecNormal = bIs3D ? this.allocateNewPoint3D() : this.allocateNewPoint2D();
		for(let index = this.points.length - 1; index >= iEndValue; index--)
		{
			let pPoint1 = this.points[index];
			let iPrevIndex = (index - 1);
			if(iPrevIndex < 0)
				iPrevIndex += this.points.length;
			let pPoint2 = this.points[iPrevIndex];

			let iPrevIndex2 = (index - 2);
			if(iPrevIndex2 < 0)
				iPrevIndex2 += this.points.length;
			let pPoint3 = this.points[iPrevIndex2];

			if(pPoint1.type === POINT_TYPE.BEZIER || pPoint2.type === POINT_TYPE.BEZIER || pPoint3.type === POINT_TYPE.BEZIER)
				continue;

			let vecToPoint = pPoint3.point.subtract(pPoint1.point);

			if(bIs3D)
			{
				let vecToMidPoint = pPoint1.point.subtract(pPoint2.point);
				fDist = BABYLON.Vector3.Cross(vecToPoint, vecToMidPoint).length()/vecToPoint.length();
			}
			else
			{
				let vecToMidPoint = pPoint2.point.subtract(pPoint1.point);
				vecToPoint.normalize();

				vecNormal.x = vecToPoint.y;
				vecNormal.y = -vecToPoint.x;
				fDist = Math.abs(BABYLON.Vector2.Dot(vecToMidPoint, vecNormal));
			}
	
			if(fDist < collinearEpsilon)
			{
				this.points.splice(iPrevIndex, 1);

				if(this.points.length <= 2)
					break;
			}
		}
		this.resetCaches();
	}

	getLinearPoints3D(bAddPointForClosed = false, fResampleError = DEFAULT_RESAMPLE_ERROR)
	{
		return this.getLinearPoints(true, false, null, fResampleError);
	}

	getLinearPoints(bAsVector3 = false, bAddPointForClosed = false, planeOrientation = 'xy', fResampleError = DEFAULT_RESAMPLE_ERROR)
	{
		if(this.cachedLinearPoints && this.cachedLinearPointParams.bAsVector3 === bAsVector3 
			&& this.cachedLinearPointParams.bAddPointForClosed === bAddPointForClosed
			&& this.cachedLinearPointParams.planeOrientation === planeOrientation
			&& this.cachedLinearPointParams.fResampleError === fResampleError)
		{
			return this.cachedLinearPoints;
		}

		let funcConvertPoint = (pPoint) =>
		{
			if(bAsVector3)
			{
				if(planeOrientation == 'zy')
					return this.allocateNewPoint3D(0, pPoint.y, pPoint.x);
				else if(planeOrientation == 'xz')
					return this.allocateNewPoint3D(pPoint.x, 0, pPoint.y);
				else if(planeOrientation == 'xy')
					return this.allocateNewPoint3D(pPoint.x, pPoint.y, 0);
				else
					return this.allocateNewPoint3D(pPoint.x, pPoint.y, pPoint.z);
			}
			else
				return pPoint.clone();
		}

		// Resample where necessary.
		let pPoints = [];
		for(let index = 0; index < this.points.length;)
		{
			let pPoint = this.points[index];
			if(pPoint.type === POINT_TYPE.BEZIER)
			{
				// Convert it 
				let procPoint;
				let iPrevPoint = index - 1;
				if(iPrevPoint < 0)
					iPrevPoint += this.points.length;
				let iNextPoint = (index + 1)%this.points.length;
				let iNextNextPoint = (index + 2)%this.points.length;
				let convPoints = this.convertBezierToLinear(this.points[iPrevPoint].point, pPoint.point, this.points[iNextPoint].point, this.points[iNextNextPoint].point, fResampleError);
				convPoints.forEach((vecPoint) => { 
					procPoint = funcConvertPoint(vecPoint);
					if(pPoints.length === 0 || pPoints[pPoints.length - 1].subtract(procPoint).length() > COINCIDENT_EPSILON)
						pPoints.push(procPoint); 
				});
				index += 3;
			}
			else
			{
				pPoints.push(funcConvertPoint(pPoint.point));
				index++;
			}
		}

		// Ensure the last points aren't the same:
		if(pPoints.length > 1)
		{
			let fEndsDistance = pPoints[0].subtract(pPoints[pPoints.length - 1]).length();
			if(fEndsDistance < COINCIDENT_EPSILON)
				pPoints.pop();
		}

		if(this.isClosed && pPoints.length > 0 && bAddPointForClosed)
			pPoints.push(pPoints[0].clone());

		this.cachedLinearPointParams = { bAsVector3, bAddPointForClosed, planeOrientation, fResampleError };
		this.cachedLinearPoints = pPoints;

		return pPoints;
	}

	getPointClosestToInternal(fX, fY, fZ)
	{
		let fMinDist = Number.MAX_VALUE;
		let iStartIndex = -1;
		let vecResPoint = null;

		let vecPoint = this.allocateNewPoint(fX, fY, fZ);

		const bIs3D = this.getIs3D();
		let convertedPoints = bIs3D ? this.getLinearPoints3D(true) : this.getLinearPoints(false, true);

		let minDistInfo = {};
		let distInfo;
		let iFinalCount = this.isClosed ? convertedPoints.length : convertedPoints.length - 1;
		for(let idx = 0; idx < iFinalCount; idx++)
		{
			let idxNext = (idx + 1) % convertedPoints.length;

			if(bIs3D)
				distInfo = GeoUtils.getDistanceToSegment3D(vecPoint, convertedPoints[idx], convertedPoints[idxNext]);
			else
				distInfo = GeoUtils.getDistanceToSegment2D(vecPoint, convertedPoints[idx], convertedPoints[idxNext]);

			if(distInfo.smallestDist >= 0 && distInfo.smallestDist < fMinDist)
			{
				fMinDist = distInfo.smallestDist;
				vecResPoint = distInfo.closestPoint;
				iStartIndex = idx;
				minDistInfo = distInfo;
			}
		}

		return { point: vecResPoint, index: iStartIndex, ...minDistInfo };
	}

	getPointClosestTo(fX, fY, fZ = undefined)
	{
		return this.getPointClosestToInternal(fX, fY, fZ);
	}

	scale(fScale, optCenter = null, bIntelligentScaleX = false, bIntelligentScaleY = false)
	{
		let vecScale = fScale;
		if(fScale.x === undefined)
			vecScale = this.allocateNewPoint2D(fScale, fScale);

		let vecCenter = optCenter ? optCenter : this.getCenter();

		if(bIntelligentScaleX || bIntelligentScaleY)
		{
			let splittingPlanes = this.computeSplittingPlanes();
			if(!bIntelligentScaleX)
				splittingPlanes.x = [];
			if(!bIntelligentScaleY)
				splittingPlanes.y = [];
			this.intelligentlyScale(vecScale, vecCenter, splittingPlanes);
		}
		else
		{
			this.points.forEach((pointData, index) =>
			{
				this.points[index].point = pointData.point.subtract(vecCenter).multiply(vecScale).add(vecCenter);
			});
		}

		this.resetCaches();
	}

	scale3D(fScale, optCenter)
	{
		let vecScale = fScale;
		if(fScale.x === undefined)
			vecScale = this.allocateNewPoint3D(fScale, fScale, fScale);

		let vecCenter = optCenter ? optCenter : this.getCenter();
		this.points.forEach((pointData, index) =>
		{
			this.points[index].point = pointData.point.subtract(vecCenter).multiply(vecScale).add(vecCenter);
		});

		this.resetCaches();
	}

	// TODO: Handle bezier; notice that for them, the index is off
	splitAtNearestTo(fX, fY, fZ = undefined, optEpsilon = COINCIDENT_EPSILON, funcFilter = null)
	{
		let pSplitInfo = this.getPointClosestToInternal(fX, fY, fZ);

		// Don't put vertices on top of other vertices
		if(this.points[pSplitInfo.index].point.subtract(pSplitInfo.point).length() < optEpsilon
			|| this.points[(pSplitInfo.index + 1)%this.points.length].point.subtract(pSplitInfo.point).length() < optEpsilon)
			return -1;

		if(funcFilter && !funcFilter(pSplitInfo))
			return -1;

		const insertPos = pSplitInfo.index + 1;
		const extraData = Spline.getExtraData(this.points[pSplitInfo.index]);
		this.points.splice(insertPos, 0, {point: pSplitInfo.point, type: POINT_TYPE.LINEAR});
		this.setExtraData(insertPos, extraData);
		this.resetCaches();

		return insertPos;
	}

	getSize()
	{
		let pBBox = this.getBBox();
		return pBBox.max.subtract(pBBox.min);
	}

	getCenter()
	{
		let pBBox = this.getBBox();
		return pBBox.min.add(pBBox.max).scale(0.5);
	}

	// Returns a point that is guaranteed to be inside the spline, and is reasonably
	// central.
	getCentralPointInside()
	{
		const bIs3D = this.getIs3D();
		let projectedSpline = this;
		let normal = this.getNormal();
		if(bIs3D)
			projectedSpline = this.clone().projectOntoPlane(normal);

		let vecSize = projectedSpline.getSize();
		let vecCenter = projectedSpline.getCenter();
		
		const vecStart = new BABYLON.Vector2(vecCenter.x - vecSize.x*2.0, vecCenter.y);
		const vecDir = new BABYLON.Vector2(1, 0);
		const vecPoints = projectedSpline.getLinearPoints();
		let intersections = Utils2D.getAllRayPolygonIntersections(vecStart, vecDir, vecPoints);

		if(intersections.length >= 2)
		{
			// The center is not in the polygon, pick the first two intersections:
			const t = (intersections[0].dIntersect + intersections[1].dIntersect)/2.0;
			vecCenter = vecStart.add(vecDir.scale(t));
		}

		// Now, unproject this point back:
		if(bIs3D)
		{
			let dist = BABYLON.Vector3.Dot(this.getCenter(), normal);
			let vecUpAxisOut = this.allocateNewPoint3D();
			let vecSideAxisOut = this.allocateNewPoint3D();
			Utils3D.getCoordSystemFromNormal(normal, vecUpAxisOut, vecSideAxisOut);
			vecCenter = vecSideAxisOut.scale(vecCenter.x).add(vecUpAxisOut.scale(vecCenter.y)).add(normal.scale(dist));
		}

		return vecCenter;
	}

	getBBox()
	{
		if(!this.cachedBBox)
		{
			let convertedPoints = this.getLinearPoints(false, true);
			this.cachedBBox = Utils3D.computePointsBBox(convertedPoints);
		}

		return { 
				min: this.cachedBBox.min.clone(), max: this.cachedBBox.max.clone(),

				// TODO: We should really use these everywhere to match BJS, but for backwards
				// compat, the above are also included.
				minimumWorld: this.cachedBBox.min.clone(), maximumWorld: this.cachedBBox.max.clone(),
			};
	}

	static getCurvesBBox(splinesList)
	{
		let vecMin = new BABYLON.Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
		let vecMax = new BABYLON.Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);

		let bIs3D;
		let convertedPoints;
		for(let pSpline of splinesList)
		{
			bIs3D = pSpline.getIs3D();
			if(bIs3D)
				convertedPoints = pSpline.getLinearPoints3D();
			else
				convertedPoints = pSpline.getLinearPoints();
			if(convertedPoints.length === 0)
				continue;
			
			let pBBox = Utils3D.computePointsBBox(convertedPoints);

			if(bIs3D)
			{
				Utils3D.includePoint(pBBox.min, vecMin, vecMax);
				Utils3D.includePoint(pBBox.max, vecMin, vecMax);
			}
			else
			{
				Utils2D.includePoint(pBBox.min, vecMin, vecMax);
				Utils2D.includePoint(pBBox.max, vecMin, vecMax);
			}
		}

		if(bIs3D)
			return { minimum: vecMin, maximum: vecMax };
		else
			return { 
				minimum: new BABYLON.Vector2(vecMin.x, vecMin.y), 
				maximum: new BABYLON.Vector2(vecMax.x, vecMax.y)
			};
	}

	translate(fX, fY)
	{
		this.points.forEach((pointData) =>
		{
			pointData.point.x += fX;
			pointData.point.y += fY;
		});
		this.resetCaches();
	}

	translate3D(fX, fY, fZ)
	{
		this.points.forEach((pointData) =>
		{
			pointData.point.x += fX;
			pointData.point.y += fY;
			pointData.point.z += fZ;
		});
		this.resetCaches();
	}

	rotate3D(rotMatrix, vecPivot)
	{
		if(!vecPivot)
			vecPivot = this.getCenter();

		let vecRotatedPoint = this.allocateNewPoint3D();
		for(let index = 0; index < this.points.length; index++)
		{		
			let pPoint = this.points[index].point;
			let offsetPoint = pPoint.subtract(vecPivot);

			BABYLON.Vector3.TransformCoordinatesToRef(offsetPoint, rotMatrix, vecRotatedPoint);
			vecRotatedPoint.addToRef(vecPivot, this.points[index].point);
		}
		this.resetCaches();
	}

	rotate(fDegAngle, vecPivot, funcFilter)
	{
		if(Math.abs(fDegAngle) < 1e-4)
			return;

		if(!vecPivot)
			vecPivot = this.getCenter();

		const bIs3D = this.getIs3D();

		let tempVec2 = this.allocateNewPoint2D();
		let tempVec3 = this.allocateNewPoint3D();
		for(let index = 0; index < this.points.length; index++)
		{
			if(funcFilter && !funcFilter(index))
				continue;
			let pPoint = this.points[index];
			if(bIs3D)
			{
				let offsetPoint = pPoint.point.subtract(vecPivot);
				tempVec2.copyFromFloats(offsetPoint.x, offsetPoint.z);
				let vec2DRotated = Utils2D.rotatePoint(tempVec2, fDegAngle);
				tempVec3.copyFromFloats(vec2DRotated.x, offsetPoint.y, vec2DRotated.y);
				tempVec3.addToRef(vecPivot, this.points[index].point);
			}
			else
				Utils2D.rotatePoint(pPoint.point.subtract(vecPivot), fDegAngle).addToRef(vecPivot, this.points[index].point);
		}
		this.resetCaches();
	}

	getPointList()
	{
		return this.points.map((pointData) =>
		{
			return pointData.point;
		});
	}

	computeSignedArea(opt3DNormal = null)
	{
		if(!this.getIs3D())
			return GeoUtils.calculateSignedPolygonAreaXY(this.getPointList());

		let vecNormal = opt3DNormal;
		if(!vecNormal)
		{
			vecNormal = this.getNormal();
			if(vecNormal.y < 0)
				vecNormal.scaleInPlace(-1.0);
		}

		let copySpline = this.clone();
		copySpline.projectOntoPlane(vecNormal);
		return copySpline.computeSignedArea();	
	}

	getIsClockwise(opt3DNormal = null)
	{
		// 2-point splines are neither clockwise nor counter clockwise,
		// so for consistency, we say they're clockwise.
		if(this.points.length == 2)
			return true;

		const signedArea = this.computeSignedArea(opt3DNormal);
		if (signedArea !== 0)
			return signedArea > 0;

		// TODO: Won't the code below produce inconsitent result based on curve orientation in space/rotation?
		// I.e., the same curve could be CW or CCW depending on how it's rotated.
		// when the points are collinear use a different method since the area is unreliable
		const bIs3D = this.getIs3D();

		const bbox = this.getBBox();
		const rangeX = bbox.max.x - bbox.min.x;
		const rangeY = bbox.max.y - bbox.min.y;
		const rangeZ = bIs3D ? bbox.max.z - bbox.min.z : -Infinity;

		const points = this.getLinearPoints(bIs3D, false, null);
		const direction = points[points.length - 1].subtract(points[0]);

		if (rangeX >= rangeY && rangeX >= rangeZ) {
			return bIs3D ? direction.x > 0 : direction.x < 0;
		} else if (rangeY >= rangeX && rangeY >= rangeZ) {
			return direction.y < 0;
		}

		return direction.z < 0;
	}

	getNormal()
	{
		if(this.getIs3D() && this.points.length >= 3)
		{
			for(let idx = 2; idx < this.points.length; idx++)
			{
				let vec1 = this.points[idx - 1].point.subtract(this.points[idx - 2].point);
				let vec2 = this.points[idx].point.subtract(this.points[idx - 1].point);
				vec1.normalize();
				vec2.normalize();
				let normal = BABYLON.Vector3.Cross(vec1, vec2);
				if(normal.length() > 1e-4)
				{
					normal.normalize();
					return normal;
				}
			}
		}

		return new BABYLON.Vector3(0, 1, 0);
	}

	ensureOrientation(isCW = true, opt3DNormal = null)
	{
		const bIsClockwise = this.getIsClockwise(opt3DNormal);
		if(bIsClockwise != isCW)
		{
			this.reverse();
			return true;
		}
		return false;
	}

	reverse()
	{
		this.points.reverse();

		// If we have extra data, we
		// *must* adjust them, to keep semantic
		// meaning of each edges the same; otherwise, they will be incorrect.
		if(this.points.length > 1)
		{
			const extraDataStart = Spline.getExtraData(this.points[0]);

			// We need to pop the first point data, shift all of them over, and then copy the last one to the last point:
			for(let pointIndex = 0; pointIndex < this.points.length - 1; pointIndex++)
			{
				const extraData = Spline.getExtraData(this.points[pointIndex + 1]);
				this.setExtraData(pointIndex, extraData);
			}

			this.setExtraData(this.points.length - 1, extraDataStart);
		}

		this.resetCaches();
	}

	getClosestRayIntersectionInternal(vecStart, vecDir, convertedPoints, iOptExcludeIndex = -1, fOptTolerance = undefined, fOptEndTolerance = undefined)
	{
		let fClosestRes = Number.MAX_VALUE;

		let bIs3D = false;
		if(convertedPoints.length > 0 && convertedPoints[0].z !== undefined)
			bIs3D = true;

		let currIntersectRes;
		let iClosestStartIndex = -1;
		let iFinalCount = this.isClosed ? convertedPoints.length : convertedPoints.length - 1;
		for(let idx = 0; idx < iFinalCount; idx++)
		{
			if(idx == iOptExcludeIndex)
				continue;

			let idxNext = (idx + 1) % convertedPoints.length;

			if(bIs3D)
				currIntersectRes = Utils3D.rayLineIntersection(vecStart, vecDir, convertedPoints[idx], convertedPoints[idxNext], fOptTolerance, fOptEndTolerance);
			else
				currIntersectRes = Utils2D.rayLineIntersection(vecStart, vecDir, convertedPoints[idx], convertedPoints[idxNext], fOptTolerance, fOptEndTolerance);

			if(currIntersectRes.dIntersect < fClosestRes && currIntersectRes.dIntersect > 1e-12)
			{
				fClosestRes = currIntersectRes.dIntersect;
				iClosestStartIndex = idx;
			}
		}

		return { distance: fClosestRes, index: iClosestStartIndex };
	}

	getClosestRayIntersection(vecStart, vecDir, fOptTolerance = undefined, fOptEndTolerance = undefined)
	{
		let convertedPoints = this.getLinearPoints(false, true);
		return this.getClosestRayIntersectionInternal(vecStart, vecDir, convertedPoints, -1, fOptTolerance, fOptEndTolerance);
	}

	getLineSegmentIntersection(vecStart, vecEnd, vecInputPoints = null, iOptExcludeIndex = -1, fOptTolerance = undefined)
	{
		const bIs3D = this.getIs3D();
		let convertedPoints = vecInputPoints ? vecInputPoints : this.getLinearPoints(bIs3D, true, null);

		let vecDir = vecEnd.subtract(vecStart);
		let fLength = vecDir.length();
		vecDir.normalize();

		let pRes = this.getClosestRayIntersectionInternal(vecStart, vecDir, convertedPoints, iOptExcludeIndex, fOptTolerance);
		if(pRes.distance >= fLength - SPLINE_EPSILON || pRes.distance < SPLINE_EPSILON)
			pRes.distance = Number.MAX_VALUE;

		if(pRes.distance !== Number.MAX_VALUE)
			pRes.point = vecStart.add(vecDir.scale(pRes.distance));

		return pRes;
	}

	doSplinesIntersect(other, fOptTolerance = undefined) {
		const bIs3D = this.getIs3D();
		let convertedPoints = this.getLinearPoints(bIs3D, true, null);
		const bOtherIs3D = other.getIs3D();
		let convertedOtherPoints = other.getLinearPoints(bOtherIs3D, true, null);

		let iFinalCount = other.isClosed ? convertedOtherPoints.length : convertedOtherPoints.length - 1;
		for(let idx = 0; idx < iFinalCount; idx++)
		{
			let idxNext = (idx + 1) % convertedOtherPoints.length;
			let pRes = this.getLineSegmentIntersection(convertedOtherPoints[idx], convertedOtherPoints[idxNext], convertedPoints, -1, fOptTolerance);
			if(pRes.distance !== Number.MAX_VALUE)
				return true;
		}
		if(other.isClosed) {
			for(let idx=0; idx < convertedPoints.length; idx++) {
				if(other.isPointInside(convertedPoints[idx]))
					return true;
			}
		}
		if(this.isClosed) {
			for(let idx = 0; idx < convertedOtherPoints.length; idx++) {
				if(this.isPointInside(convertedOtherPoints[idx]))
					return true;
			}
		}
		return false;
	}

	computeSplittingPlanes()
	{
		let resPlanes = {
			x: [],
			y: []
		};

		let bbox = this.getBBox();
		let vecCenter = this.getCenter();
		let vecHalfSize = this.getSize().scale(0.5);

		const PLANE_EPSILON = 0.005;
		// Offsets, in percentages, for multiple rays to shoot in one direction
		const BBOX_OFFSETS = [0.05, 0.5, 0.95];

		// Shoot rays in all dirs:
		let vecPoint;
		let vecRayOrigin = this.allocateNewPoint2D();
		let vecDir;
		let fIntersect;
		let fMinIntersect;

		fMinIntersect = Number.MAX_VALUE;
		vecDir = this.allocateNewPoint2D(-1, 0);
		for(let fOffset of BBOX_OFFSETS)
		{
			vecRayOrigin.x = vecCenter.x;
			vecRayOrigin.y = (fOffset - 0.5)/0.5*vecHalfSize.y + vecCenter.y;
			fIntersect = this.getClosestRayIntersection(vecRayOrigin, vecDir).distance;
			if(fIntersect !== Number.MAX_VALUE && fIntersect < fMinIntersect)
				fMinIntersect = fIntersect;
		}
		if(fMinIntersect === Number.MAX_VALUE)
			vecPoint = bbox.min;
		else
			vecPoint = vecCenter.add(vecDir.scale(fMinIntersect - PLANE_EPSILON));
		resPlanes.x.push(vecPoint.x);

		fMinIntersect = Number.MAX_VALUE;
		vecDir = this.allocateNewPoint2D(1, 0);
		for(let fOffset of BBOX_OFFSETS)
		{
			vecRayOrigin.x = vecCenter.x;
			vecRayOrigin.y = (fOffset - 0.5)/0.5*vecHalfSize.y + vecCenter.y;
			fIntersect = this.getClosestRayIntersection(vecRayOrigin, vecDir).distance;
			if(fIntersect !== Number.MAX_VALUE && fIntersect < fMinIntersect)
				fMinIntersect = fIntersect;
		}
		if(fMinIntersect === Number.MAX_VALUE)
			vecPoint = bbox.max;
		else
			vecPoint = vecCenter.add(vecDir.scale(fMinIntersect - PLANE_EPSILON));
		resPlanes.x.push(vecPoint.x);

		fMinIntersect = Number.MAX_VALUE;
		vecDir = this.allocateNewPoint2D(0, -1);
		for(let fOffset of BBOX_OFFSETS)
		{
			vecRayOrigin.x = (fOffset - 0.5)/0.5*vecHalfSize.x + vecCenter.x;
			vecRayOrigin.y = vecCenter.y;
			fIntersect = this.getClosestRayIntersection(vecRayOrigin, vecDir).distance;
			if(fIntersect !== Number.MAX_VALUE && fIntersect < fMinIntersect)
				fMinIntersect = fIntersect;
		}
		if(fMinIntersect === Number.MAX_VALUE)
			vecPoint = bbox.min;
		else
			vecPoint = vecCenter.add(vecDir.scale(fMinIntersect - PLANE_EPSILON));
		resPlanes.y.push(vecPoint.y);

		fMinIntersect = Number.MAX_VALUE;
		vecDir = this.allocateNewPoint2D(0, 1);
		for(let fOffset of BBOX_OFFSETS)
		{
			vecRayOrigin.x = (fOffset - 0.5)/0.5*vecHalfSize.x + vecCenter.x;
			vecRayOrigin.y = vecCenter.y;
			fIntersect = this.getClosestRayIntersection(vecRayOrigin, vecDir).distance;
			if(fIntersect !== Number.MAX_VALUE && fIntersect < fMinIntersect)
				fMinIntersect = fIntersect;
		}
		if(fMinIntersect === Number.MAX_VALUE)
			vecPoint = bbox.max;
		else
			vecPoint = vecCenter.add(vecDir.scale(fMinIntersect - PLANE_EPSILON));
		resPlanes.y.push(vecPoint.y);

		return resPlanes;
	}

	computeStretchSegmentScaling(singleDimPlanes, currMin, currMax, targetSize, targetCenter)
	{
		if(!singleDimPlanes || singleDimPlanes.length === 0)
			return {scale: targetSize/(currMax - currMin), cumulOffsets: null};

		// Compute current sizes between planes, including bbox edges
		let planeDistances = [];
		singleDimPlanes.forEach((planeCoord, index) =>
		{
			let iDist;
			if(index === 0)
				iDist = planeCoord - currMin;
			else
				iDist = planeCoord - singleDimPlanes[index - 1];
			planeDistances.push(iDist);
		});

		let iDist = currMax - singleDimPlanes[singleDimPlanes.length - 1];
		planeDistances.push(iDist);

		// Compute total fixed and stretchable distances
		let distToScale = 0;
		let distFixed = 0;
		for(let idx = 0; idx < planeDistances.length; idx++)
		{
			if(idx % 2 === 0)
				distFixed += planeDistances[idx];
			else
				distToScale += planeDistances[idx];
		}

		// Compute the new scale and new distances
		let fStretchSegmentsScale = (targetSize - distFixed)/distToScale;
		let newDistances = [];
		for(let idx = 0; idx < planeDistances.length; idx++)
		{
			if(idx % 2 === 0)
				newDistances[idx] = planeDistances[idx];
			else
				newDistances[idx] = planeDistances[idx]*fStretchSegmentsScale;
		}

		// Now, compute offsets relative to the scaling center:
		// Use new center
		// Find where it belongs in the array
		let iPlaneDistCenterIndex = 0;
		let inSegmentCenterInterp = 0;
		let fCurrPlaneCoord = currMin;
		let fNextPlaneCoord;
		for(let idx = 0; idx < planeDistances.length; idx++)
		{
			fNextPlaneCoord = fCurrPlaneCoord + planeDistances[idx];
			if(targetCenter < fNextPlaneCoord)
			{
				iPlaneDistCenterIndex = idx;

				// First, figure out an interpolation for the segment the center is in:
				inSegmentCenterInterp = (targetCenter - fCurrPlaneCoord)/(fNextPlaneCoord - fCurrPlaneCoord);

				break;
			}
			fCurrPlaneCoord = fNextPlaneCoord;
		}

		// Now, compute cumulative distances to offset by in each direction
		// from that point.
		let leftDistArray = [];
		let rightDistArray = [];
		let leftScaleBoundaries = [];
		let rightScaleBoundaries = [];

		// If our segment the strech thing is in scales, compute the offset:
		let fCumulOffset;
		let fCumulBoundary;
		fCumulOffset = (1.0 - inSegmentCenterInterp)*(newDistances[iPlaneDistCenterIndex] - planeDistances[iPlaneDistCenterIndex]);
		fCumulBoundary = (1.0 - inSegmentCenterInterp)*newDistances[iPlaneDistCenterIndex];
		for(let idx = iPlaneDistCenterIndex + 1; idx < newDistances.length; idx++)
		{
			rightDistArray.push(fCumulOffset);
			rightScaleBoundaries.push(fCumulBoundary);
			fCumulOffset += (newDistances[idx] - planeDistances[idx]);
			fCumulBoundary += newDistances[idx];
		}

		fCumulOffset = inSegmentCenterInterp*(newDistances[iPlaneDistCenterIndex] - planeDistances[iPlaneDistCenterIndex]);
		fCumulBoundary = inSegmentCenterInterp*newDistances[iPlaneDistCenterIndex];
		for(let idx = iPlaneDistCenterIndex - 1; idx >= 0; idx--)
		{
			leftDistArray.push(-fCumulOffset);
			leftScaleBoundaries.push(-fCumulBoundary);
			fCumulOffset += (newDistances[idx] - planeDistances[idx]);
			fCumulBoundary += newDistances[idx];
		}

		leftDistArray.reverse();

		return {scale: fStretchSegmentsScale, cumulOffsets: [...leftDistArray, 0, ...rightDistArray], cumulBoundaries: [...leftScaleBoundaries, targetCenter, ...rightScaleBoundaries]};
	}

	applyStretchSegmentScaling(strAxis, fPlainScale, splittingPlanesValues, currMin, currMax, segmentsInfo, fCenter)
	{
		if(splittingPlanesValues && splittingPlanesValues.length > 0)
		{
			let fPrevCenter = (currMin + currMax)/2.0;
			let coordsCopy = this.points.map((pointData) => { return pointData.point[strAxis]; });

			let prevValue = currMin - 1;
			for(let iSplitPlane = 0; iSplitPlane <= splittingPlanesValues.length; iSplitPlane++)
			{
				let fNextValue = iSplitPlane < splittingPlanesValues.length ? splittingPlanesValues[iSplitPlane] : (currMax + 1);
				coordsCopy.forEach((fCoordinate, index) =>
				{
					// See if we match the plane
					if(!(fCoordinate >= prevValue && fCoordinate < fNextValue))
						return;

					// Apply shift + scale
					if(iSplitPlane % 2 !== 0)
					{
						// Scale it, but scale it relative to the beginning of the segment
						this.points[index].point[strAxis] = (this.points[index].point[strAxis] - segmentsInfo.cumulBoundaries[iSplitPlane])*segmentsInfo.scale + segmentsInfo.cumulBoundaries[iSplitPlane];
					}

					this.points[index].point[strAxis] += segmentsInfo.cumulOffsets[iSplitPlane];
				});

				prevValue = splittingPlanesValues[iSplitPlane];
			}

			// We need to re-center it now:
			let fNewMin = Number.MAX_VALUE;
			let fNewMax = -Number.MAX_VALUE;
			this.points.forEach((pointInfo) =>
			{
				const fValue = pointInfo.point[strAxis];
				if(fValue < fNewMin)
					fNewMin = fValue;
				if(fValue > fNewMax)
					fNewMax = fValue;
			});

			const fNewCenter = (fNewMax + fNewMin)/2.0;
			const fOffset = fNewCenter - fPrevCenter;
			this.points.forEach((pointInfo) =>
			{
				pointInfo.point[strAxis] -= fOffset;
			});

		}
		else
		{
			this.points.forEach((pointData, index) =>
			{
				this.points[index].point[strAxis] = (pointData.point[strAxis] - fCenter)*fPlainScale + fCenter;
			});
		}
	}

	intelligentlyScale(vecScale, vecCenter, splittingPlanes)
	{
		let bbox = this.getBBox();
		let vecNewSize = bbox.max.subtract(bbox.min).multiply(vecScale);

		// Now, scale the odd segments evenly
		const xInfo = this.computeStretchSegmentScaling(splittingPlanes.x, bbox.min.x, bbox.max.x, vecNewSize.x, vecCenter.x);
		const yInfo = this.computeStretchSegmentScaling(splittingPlanes.y, bbox.min.y, bbox.max.y, vecNewSize.y, vecCenter.y);

		this.applyStretchSegmentScaling('x', vecScale.x, splittingPlanes.x, bbox.min.x, bbox.max.x, xInfo, vecCenter.x);
		this.applyStretchSegmentScaling('y', vecScale.y, splittingPlanes.y, bbox.min.y, bbox.max.y, yInfo, vecCenter.y);
	}

	mirrorHor()
	{
		// TODO
	}

	mirrorVert()
	{
		// TODO
	}

	static getEllipticalResampleAngleInRadians(fTolerance, fRadius)
	{
		const fMinSamples = 16;
		let fAngleIncrement = 1.0 - fTolerance*fTolerance/(2.0*fRadius*fRadius);
		if(fAngleIncrement > 1.0 || fAngleIncrement < -1.0)
			fAngleIncrement = 2.0*Math.PI/fMinSamples;
		else
			fAngleIncrement = 8.0*Math.acos(fAngleIncrement);

		if(Math.floor(2.0*Math.PI / fAngleIncrement) + 1 < fMinSamples)
			fAngleIncrement = 2.0*Math.PI/fMinSamples;

		if(fAngleIncrement < 0.1/180.0*Math.PI)
			fAngleIncrement = 0.1/180.0*Math.PI;
		return fAngleIncrement;
	}

	static createArc(fRadiusX, fRadiusY = null, fStartAngle = 0, fEndAngle = 360, vecCenter = null, fTolerance = 0.01)
	{
		if(!fRadiusY)
			fRadiusY = fRadiusX;
		let fRadSampleAngle = Spline.getEllipticalResampleAngleInRadians(fTolerance, Math.max(fRadiusX, fRadiusY));

		let fRadStartAngle = fStartAngle/180.0*Math.PI;
		let fRadEndAngle = fEndAngle/180.0*Math.PI;

		let pointsOut = [];
		let vecCurrPoint = BABYLON.Vector2.Zero();
		for(let fCurrAngle = fRadStartAngle; fCurrAngle <= fRadEndAngle; )
		{
			vecCurrPoint.x = fRadiusX*Math.cos(fCurrAngle);
			vecCurrPoint.y = fRadiusY*Math.sin(fCurrAngle);

			if(vecCenter)
			{
				vecCurrPoint.x += vecCenter.x;
				vecCurrPoint.y += vecCenter.y;
			}

			pointsOut.push({ point: vecCurrPoint.clone(), type: POINT_TYPE.LINEAR });
			
			if(fCurrAngle < fRadEndAngle && fCurrAngle + fRadSampleAngle > fRadEndAngle)
				fCurrAngle = fRadEndAngle;
			else
				fCurrAngle += fRadSampleAngle;
		}

		const bIsClosed = pointsOut.length > 1 && pointsOut[pointsOut.length - 1].point.subtract(pointsOut[0].point).length() < COINCIDENT_EPSILON;
		if(bIsClosed)
			pointsOut.pop();

		return new Spline({ points: pointsOut, isClosed: bIsClosed });
	}

	static createLine(vecStart, vecEnd, iNumPoints = 2)
	{
		let pTempSpline = new Spline({
			points: [
				{ point: vecStart, type: POINT_TYPE.LINEAR },
				{ point: vecEnd, type: POINT_TYPE.LINEAR }
			],
			isClosed: false
		});

		if(iNumPoints <= 2)
			return pTempSpline;

		return pTempSpline.resampleByNumber(iNumPoints);
	}

	static createRectangle(fWidth, fHeight, bIsClosed = true, fCornerRadius = 0.0, fTolerance = 0.01, bTopOnly = false)
	{
		const fHalfWidth = fWidth/2;
		const fHalfHeight = fHeight/2;

		let pResSpline;
		if(fCornerRadius < 1e-4)
		{
			const rectShape = {"points":
				[
					{"point": {"x": -fHalfWidth, "y": -fHalfHeight}, "type": POINT_TYPE.LINEAR},
					{"point": {"x": -fHalfWidth, "y": fHalfHeight}, "type": POINT_TYPE.LINEAR},
					{"point": {"x": fHalfWidth, "y": fHalfHeight}, "type": POINT_TYPE.LINEAR},
					{"point": {"x": fHalfWidth, "y": -fHalfHeight}, "type": POINT_TYPE.LINEAR}
				],
				"isClosed": (bIsClosed && !bTopOnly)
			};

			pResSpline = new Spline(rectShape);
		}
		else
		{
			let pTopLeftCorner = Spline.createArc(fCornerRadius, fCornerRadius,  90, 180, new BABYLON.Vector2(-fHalfWidth + fCornerRadius, fHalfHeight - fCornerRadius), fTolerance);
			let pTopRightCorner = Spline.createArc(fCornerRadius, fCornerRadius,  0, 90, new BABYLON.Vector2(fHalfWidth - fCornerRadius, fHalfHeight - fCornerRadius), fTolerance);
			let pBottomRightCorner = Spline.createArc(fCornerRadius, fCornerRadius,  270, 360, new BABYLON.Vector2(fHalfWidth - fCornerRadius, -fHalfHeight + fCornerRadius), fTolerance);
			let pBottomLeftCorner = Spline.createArc(fCornerRadius, fCornerRadius,  180, 270, new BABYLON.Vector2(-fHalfWidth + fCornerRadius, -fHalfHeight + fCornerRadius), fTolerance);

			if(bTopOnly)
			{
				pTopRightCorner.attach(pTopLeftCorner, true, false, true);
				pResSpline = pTopRightCorner;
			}
			else
			{
				pBottomLeftCorner.attach(pBottomRightCorner, true, false, true);
				pBottomLeftCorner.attach(pTopRightCorner, true, false, true);
				pBottomLeftCorner.attach(pTopLeftCorner, true, false, true);
				pResSpline = pBottomLeftCorner;
			}

			pResSpline.setIsClosed(bIsClosed);
		}

		return pResSpline;
	}

	static createSpiral(fRadiusX, fRadiusY, dNumTurns, fFinalRadiusPerc, fTolerance = 0.01, strOrientation = 'xz', bIsExponential = false, fExponentBase = Math.E, 
						fCenterX = 0, fCenterY = 0, fCenterZ = 0, fStartElevation = 0, fEndElevation = 0)
	{
		const fStartAngle = 0;
		const fEndAngle = Math.PI*2.0*dNumTurns;

		let fRadSampleAngle = Spline.getEllipticalResampleAngleInRadians(fTolerance, Math.max(fRadiusX, fRadiusY));

		let funcConvertPoint = (pPoint) =>
		{
			if(strOrientation == 'zy')
				return new BABYLON.Vector3(pPoint.z, pPoint.y, pPoint.x);
			else if(strOrientation == 'xz')
				return new BABYLON.Vector3(pPoint.x, pPoint.z, pPoint.y);
			else
				return new BABYLON.Vector3(pPoint.x, pPoint.y, pPoint.z);
		}

		let pointsOut = [];
		let fCurrAngle;
		let vecCurrPoint = BABYLON.Vector3.Zero();
		if(bIsExponential)
		{
			let fInterp;
			let fTempPow;
			let svFinalRadius = new BABYLON.Vector2(fRadiusX*fFinalRadiusPerc, fRadiusY*fFinalRadiusPerc);
			fExponentBase *= Math.E;
			const fLogOfNumTurns = Math.log(dNumTurns);
			const fPowerWithBase = Math.pow(fExponentBase, fLogOfNumTurns);
			for(fCurrAngle = fStartAngle; fCurrAngle <= fEndAngle; fCurrAngle += fRadSampleAngle)
			{
				fInterp = (Math.pow(fExponentBase, fLogOfNumTurns*fCurrAngle/fEndAngle) - 1)/fPowerWithBase;

				fTempPow = fInterp*(fRadiusX - svFinalRadius.x) + svFinalRadius.x;
				vecCurrPoint.x = fTempPow*Math.cos(fCurrAngle);
				
				fTempPow = fInterp*(fRadiusY - svFinalRadius.y) + svFinalRadius.y;
				vecCurrPoint.y = fTempPow*Math.sin(fCurrAngle);

				vecCurrPoint.z = fInterp*(fStartElevation - fEndElevation) + fEndElevation;

				let vecFinalPoint = funcConvertPoint(vecCurrPoint);
				vecFinalPoint.x += fCenterX;
				vecFinalPoint.y += fCenterY;
				vecFinalPoint.z += fCenterZ;

				pointsOut.push({point: vecFinalPoint, type: POINT_TYPE.LINEAR});
			}
		}
		else
		{
			let svFinalRadius = new BABYLON.Vector2(fRadiusX*fFinalRadiusPerc, fRadiusY*fFinalRadiusPerc);
			
			let svStartRadius = new BABYLON.Vector2(fRadiusX, fRadiusY);
			let svCurrRadius = svFinalRadius.clone();

			const fInterpMult = 1.0/((Math.PI*2.0/fRadSampleAngle)*dNumTurns);
			let svRadiiDeltaChange = svStartRadius.subtract(svFinalRadius).scale(fInterpMult);
			let fElevationDeltaChange = (fStartElevation - fEndElevation)*fInterpMult;
			let fCurrZ = fEndElevation;
			for(fCurrAngle = fStartAngle; fCurrAngle <= fEndAngle; fCurrAngle += fRadSampleAngle)
			{
				vecCurrPoint.x = svCurrRadius.x*Math.cos(fCurrAngle);
				vecCurrPoint.y = svCurrRadius.y*Math.sin(fCurrAngle);
				vecCurrPoint.z = fCurrZ;

				svCurrRadius.addInPlace(svRadiiDeltaChange);
				fCurrZ += fElevationDeltaChange;

				let vecFinalPoint = funcConvertPoint(vecCurrPoint);
				vecFinalPoint.x += fCenterX;
				vecFinalPoint.y += fCenterY;
				vecFinalPoint.z += fCenterZ;

				pointsOut.push({point: vecFinalPoint, type: POINT_TYPE.LINEAR});
			}
		}

		return new Spline({ points: pointsOut, isClosed: false });
	}

	static computeDistancesOnPiecewiseLinear(linearPointsIn, distancesOut, bIsClosed)
	{
		let fTotalLength = 0;
		if(distancesOut)
			distancesOut.push(fTotalLength);

		let iNumPoints = linearPointsIn.length;
		let iEndPoint = bIsClosed ? iNumPoints + 1 : iNumPoints;
		for(let iCurrPoint = 1; iCurrPoint < iEndPoint; iCurrPoint++)
		{
			fTotalLength += linearPointsIn[iCurrPoint % iNumPoints].subtract(linearPointsIn[(iCurrPoint - 1) % iNumPoints]).length();
			if(distancesOut)
				distancesOut.push(fTotalLength);
		}

		return fTotalLength;
	}

	static computeSharpPerPointDirections(pPointsIn, bIsClosed = false)
	{
		let dirsOut = [];
		let iNumPoints = pPointsIn.length;
		if(iNumPoints < 2)
			return dirsOut;

		let iEndPoint = bIsClosed ? iNumPoints : iNumPoints - 1;
		for(let iCurrPoint = 0; iCurrPoint < iEndPoint; iCurrPoint++)
		{
			const iNextPoint = (iCurrPoint + 1)%iNumPoints;
			let vecDir = pPointsIn[iNextPoint].subtract(pPointsIn[iCurrPoint]);
			vecDir.normalize();
			dirsOut.push(vecDir);
		}

		if(!bIsClosed)
			dirsOut.push(dirsOut[dirsOut.length - 1].clone());

		return dirsOut;
	}

	static computeSmoothedPerPointDirections(pPointsIn, bIsClosed = false)
	{
		let iNumPonts = pPointsIn.length;
		if(iNumPonts <= 1)
			return [];

		let perPointDirs = [];
		let vecDir1, vecDir2, vecAvgDir;

		// First normal is special
		if(bIsClosed)
		{
			vecDir1 = pPointsIn[0].subtract(pPointsIn[pPointsIn.length - 1]);
			vecDir1.normalize();
			vecDir2 = pPointsIn[1].subtract(pPointsIn[0]);
			vecDir2.normalize();
			vecAvgDir = vecDir1.add(vecDir2).scale(0.5);
		}
		else
		{
			vecAvgDir = pPointsIn[1].subtract(pPointsIn[0]);
		}
		vecAvgDir.normalize();
		perPointDirs.push(vecAvgDir);

		for(let iCurrPoint = 1; iCurrPoint < iNumPonts - 1; iCurrPoint++)
		{
			vecDir1 = pPointsIn[iCurrPoint].subtract(pPointsIn[iCurrPoint - 1]);
			vecDir1.normalize();

			vecDir2 = pPointsIn[iCurrPoint + 1].subtract(pPointsIn[iCurrPoint]);
			vecDir2.normalize();

			vecAvgDir = vecDir1.add(vecDir2).scale(0.5);
			vecAvgDir.normalize();
			perPointDirs.push(vecAvgDir);
		}

		// Last point is special also
		if(bIsClosed)
		{
			vecDir1 = pPointsIn[0].subtract(pPointsIn[iNumPonts - 1]);
			vecDir1.normalize();
			vecDir2 = pPointsIn[iNumPonts - 1].subtract(pPointsIn[iNumPonts - 2]);
			vecDir2.normalize();
			vecAvgDir = vecDir1.add(vecDir2).scale(0.5);
		}
		else
		{
			vecAvgDir = pPointsIn[iNumPonts - 1].subtract(pPointsIn[iNumPonts - 2]);
		}
		vecAvgDir.normalize();
		perPointDirs.push(vecAvgDir);

		return perPointDirs;
	}

	resampleByDistance(fSpacing, startPerc = 0.0, endPerc = 1.0, preserveCornersDegAngle = null, lastPointTreatment = RESAMPLE_LAST_POINT_OPTION.DELETE, directionsOut = null)
	{
		let vecDistances = [];
		let vecLinearPoints = this.getLinearPoints();
		let fTotalDist = Spline.computeDistancesOnPiecewiseLinear(vecLinearPoints, vecDistances, this.getIsClosed());

		return this.resampleByDistanceInternal(fSpacing, vecLinearPoints, fTotalDist, vecDistances, directionsOut, lastPointTreatment, preserveCornersDegAngle, startPerc, endPerc);
	}

	resampleByNumber(iNumPoints, startPerc = 0.0, endPerc = 1.0, preserveCornersDegAngle = null, directionsOut = null)
	{
		let vecDistances = [];
		let vecLinearPoints = this.getLinearPoints();
		let fTotalDist = Spline.computeDistancesOnPiecewiseLinear(vecLinearPoints, vecDistances, this.getIsClosed());
		if(iNumPoints < 1)
			iNumPoints = 1;

		let fUltimateDist = fTotalDist/2.0;
		if(iNumPoints > 1)
		{
			// For closed shapes, if we divide by -1, the last point would be
			// at the same position as the first point, and would be automatically deleted.
			if(this.getIsClosed())
				fUltimateDist = fTotalDist/iNumPoints;
			else
				fUltimateDist = fTotalDist/(iNumPoints - 1);
		}
		return this.resampleByDistanceInternal(fUltimateDist, vecLinearPoints, fTotalDist, vecDistances, directionsOut, RESAMPLE_LAST_POINT_OPTION.ADD_ORIGINAL, preserveCornersDegAngle, startPerc, endPerc);
	}

	static computePointAngleCos(linearPointsIn, iCurrPoint, bIsClosed, cachedVec1 = null, cachedVec2 = null)
	{
		const iNumPoints = linearPointsIn.length;

		const bIs3D = linearPointsIn[iCurrPoint % iNumPoints].z !== undefined;
		let vecDir1 = cachedVec1;
		if(!vecDir1)
			vecDir1 = bIs3D ? BABYLON.Vector3.Zero() : BABYLON.Vector2.Zero();
		let vecDir2 = cachedVec2;
			vecDir2 = bIs3D ? BABYLON.Vector3.Zero() : BABYLON.Vector2.Zero();

		let iPrevPoint = (iCurrPoint - 1) % iNumPoints;
		if(iPrevPoint < 0)
		{
			if(bIsClosed)
				iPrevPoint = iNumPoints - 1;
			else
				return 1;
		}
		linearPointsIn[iCurrPoint % iNumPoints].subtractToRef(linearPointsIn[iPrevPoint], vecDir1);
		linearPointsIn[(iCurrPoint + 1) % iNumPoints].subtractToRef(linearPointsIn[iCurrPoint % iNumPoints], vecDir2);
		vecDir1.normalize();
		vecDir2.normalize();

		let fDotProd;
		if(bIs3D)
			fDotProd = BABYLON.Vector3.Dot(vecDir1, vecDir2);
		else
			fDotProd = BABYLON.Vector2.Dot(vecDir1, vecDir2);

		return fDotProd;
	}

	resampleByDistanceInternal(fDesiredSpacing, linearPointsIn, fTotalLength, rawPointDistances, directionsOut = null, eLastPointTreatment = RESAMPLE_LAST_POINT_OPTION.ADD_ORIGINAL, preserveCornersDegAngle = null, 
								startPerc = 0.0, endPerc = 1.0)
	{
		let perPointDirections = null;
		if(directionsOut)
			perPointDirections = Spline.computeSmoothedPerPointDirections(linearPointsIn);

		let pointInfosOut = [];

		let vecTemp;

		let iCurrPoint, iNumPoints = linearPointsIn.length;

		let fStartLengthPos = startPerc*fTotalLength;
		let fEndLengthPos = endPerc*fTotalLength;

		if(fDesiredSpacing < 0.001)
			fDesiredSpacing = 0.001;

		// Re-adjust spacing
		let iNumProcessedPoints = fTotalLength/fDesiredSpacing;
		fDesiredSpacing = (fEndLengthPos - fStartLengthPos)/iNumProcessedPoints;

		let fCurrDistance = fDesiredSpacing;
		if(fStartLengthPos > 0.0)
			fCurrDistance = fStartLengthPos;
		let fInterp;

		const bIs3D = this.getIs3D();

		let preserveCornersCosine = undefined;
		if(preserveCornersDegAngle !== undefined && preserveCornersDegAngle !== null)
			preserveCornersCosine = Math.cos(preserveCornersDegAngle/180.0*Math.PI);

		let vecDir1 = bIs3D ? this.allocateNewPoint3D() : this.allocateNewPoint2D();
		let vecDir2 = bIs3D ? this.allocateNewPoint3D() : this.allocateNewPoint2D();

		const bIsClosed = this.getIsClosed();
		let iEndPoint = bIsClosed ? iNumPoints + 1 : iNumPoints;

		let funcAddPoint = (fCumulDistanceOnPath) => {
				const iPrevPoint = (iCurrPoint - 1) % iNumPoints;

				// Now, add an interpolated point.
				fInterp = (fCumulDistanceOnPath - rawPointDistances[iCurrPoint - 1])/(rawPointDistances[iCurrPoint] - rawPointDistances[iCurrPoint - 1]);

				if(bIs3D)
					vecTemp = BABYLON.Vector3.Lerp(linearPointsIn[iPrevPoint], linearPointsIn[iCurrPoint % iNumPoints], fInterp);
				else
					vecTemp = BABYLON.Vector2.Lerp(linearPointsIn[iPrevPoint], linearPointsIn[iCurrPoint % iNumPoints], fInterp);

				// Make sure we don't add any duplicate points if the distances coincide with segment sizes
				if(vecTemp.subtract(pointInfosOut[pointInfosOut.length - 1].point).length() > 1e-6)
				{
					pointInfosOut.push({ point: vecTemp, type: POINT_TYPE.LINEAR });

					// NOTE: These are *smoothed* directions, which may not be what we're looking for when resampling sharp-cornered curves such
					// as rectangles with just the corner points.
					if(directionsOut)
					{
						if(bIs3D)
							vecTemp = BABYLON.Vector3.Lerp(perPointDirections[iPrevPoint], perPointDirections[iCurrPoint % iNumPoints], fInterp);
						else
							vecTemp = BABYLON.Vector2.Lerp(perPointDirections[iPrevPoint], perPointDirections[iCurrPoint % iNumPoints], fInterp);
						directionsOut.push(vecTemp);
					}
				}		
		};

		// Append any points we might need to before the start
		iCurrPoint = 1;
		if(fStartLengthPos > 0.0)
		{
			for(; iCurrPoint < iEndPoint; iCurrPoint++)
			{
				const iPrevPoint = (iCurrPoint - 1) % iNumPoints;
				pointInfosOut.push({ point: linearPointsIn[iPrevPoint], type: POINT_TYPE.LINEAR });
				if(directionsOut)
					directionsOut.push(perPointDirections[iPrevPoint]);
				fCurrDistance = rawPointDistances[iCurrPoint];

				if(rawPointDistances[iCurrPoint] > fStartLengthPos)
				{
					fCurrDistance = fStartLengthPos;
					funcAddPoint(fStartLengthPos);
					break;
				}
			}
		}
		else
		{
			pointInfosOut.push({ point: linearPointsIn[0].clone(), type: POINT_TYPE.LINEAR });
			if(directionsOut)
				directionsOut.push(perPointDirections[0].clone());
		}

		let bStopLoopIter = false;
		for(; iCurrPoint < iEndPoint; iCurrPoint++)
		{
			if(preserveCornersDegAngle !== undefined)
			{
				let fDotProd = Spline.computePointAngleCos(linearPointsIn, iCurrPoint, bIsClosed, vecDir1, vecDir2);
				if(fDotProd + 1e-4 >= preserveCornersCosine)
					fCurrDistance = rawPointDistances[iCurrPoint - 1];
			}

			while(fCurrDistance <= rawPointDistances[iCurrPoint])
			{
				if(endPerc < 1.0 && fCurrDistance > fEndLengthPos)
				{
					funcAddPoint(fEndLengthPos);
					if(fEndLengthPos < rawPointDistances[iCurrPoint - 1])
						iCurrPoint--;
					bStopLoopIter = true;
					break;
				}

				funcAddPoint(fCurrDistance);
				fCurrDistance += fDesiredSpacing;
			}

			if(bStopLoopIter)
				break;
		}

		if(endPerc < 1.0)
		{
			// Append the rest of the original points
			if(iCurrPoint >= iNumPoints)
				iCurrPoint = iNumPoints - 1;
			for(; iCurrPoint < iEndPoint; iCurrPoint++)
			{
				pointInfosOut.push({ point: linearPointsIn[iCurrPoint % iNumPoints], type: POINT_TYPE.LINEAR });
				if(directionsOut)
					directionsOut.push(perPointDirections[iCurrPoint % iNumPoints]);
			}
		
		}
		else
		{
			let fLastPointDistance = (iNumPoints >= 2) ? rawPointDistances[iEndPoint - 1] - (fCurrDistance - fDesiredSpacing) : 0.0;			
			if(eLastPointTreatment != RESAMPLE_LAST_POINT_OPTION.DELETE && iNumPoints >= 2 && fLastPointDistance < fDesiredSpacing && fLastPointDistance >= 1e-3)
			{
				const iEndIndex = (iEndPoint - 1)  % iNumPoints;
				if(eLastPointTreatment === RESAMPLE_LAST_POINT_OPTION.ADD_ORIGINAL)
					pointInfosOut.push({ point: linearPointsIn[iEndIndex].clone(), type: POINT_TYPE.LINEAR });
				else
				{
					const iBeforeEndIndex = (iEndPoint - 2)  % iNumPoints;
					let lastDir = linearPointsIn[iEndIndex].subtract(linearPointsIn[iBeforeEndIndex]);
					lastDir.normalize();
					let lastPoint = pointInfosOut[pointInfosOut.length - 1].point.add(lastDir.scale(fDesiredSpacing));
					pointInfosOut.push({ point: lastPoint, type: POINT_TYPE.LINEAR });
				}

				if(directionsOut)
					directionsOut.push(perPointDirections[iEndIndex].clone());
			}
		}

		if(this.getIsClosed() && pointInfosOut.length > 2)
		{
			if(pointInfosOut[0].point.subtract(pointInfosOut[pointInfosOut.length - 1].point).length() < 1e-6)
			{
				pointInfosOut.pop();
				if(directionsOut)
					directionsOut.pop();
			}
		}

		return new Spline({points: pointInfosOut, isClosed: this.getIsClosed()});
	}

	concatSplinesHelper(pFirstSpline, pSecondSpline, bKeepSplinesInPlace = false, bConnectViaExtraLine = false)
	{
		let bIsFirst3D = pFirstSpline.getIs3D();
		let bIsSecond3D = pSecondSpline.getIs3D();

		if(bIsFirst3D != bIsSecond3D)
			throw Error('One of the curves is 2D, while the other one is 3D. This currently not supported.');

		// Attach the splines end-to-end.
		let points1 = pFirstSpline.points;
		let points2 = pSecondSpline.points.slice();

		let vecDiff = this.allocateNewPoint3D();
		if(points1.length > 0 && points2.length > 0)
		{
			vecDiff.x = points1[points1.length - 1].point.x - points2[0].point.x;
			vecDiff.y = points1[points1.length - 1].point.y - points2[0].point.y;
			if(bIsFirst3D && bIsSecond3D)
				vecDiff.z = points1[points1.length - 1].point.z - points2[0].point.z;
		}

		if(!bKeepSplinesInPlace)
		{
			points2.forEach((pointInfo) => {
				pointInfo.point.x += vecDiff.x;
				pointInfo.point.y += vecDiff.y;
				if(bIsFirst3D && bIsSecond3D)
					pointInfo.point.z += vecDiff.z;
			});
		}

		let bDeleteFirstPoint = true;
		if(bKeepSplinesInPlace && bConnectViaExtraLine)
			bDeleteFirstPoint = vecDiff.length() <= COINCIDENT_EPSILON;

		if(bDeleteFirstPoint)
			points2.shift();

		let resPoints = points1.concat(points2);
		
		// TODO: Ensure we uniform in terms of 2D or 3D
		return resPoints;
	}

	attach(pOtherSpline, bAttachToSelfEnd = true, bAttachToOtherEnd = false, bKeepSplinesInPlace = false, bConnectViaExtraLine = false)
	{
		if((bAttachToSelfEnd && bAttachToOtherEnd) || (!bAttachToSelfEnd && !bAttachToOtherEnd))
			pOtherSpline.reverse();

		let pFirstCurve = bAttachToSelfEnd ? this : pOtherSpline;
		let pSecondCurve = bAttachToSelfEnd ? pOtherSpline : this;

		this.points = this.concatSplinesHelper(pFirstCurve, pSecondCurve, bKeepSplinesInPlace, bConnectViaExtraLine);
		this.resetCaches();
	}

	// Given a 2d spline in world space coords and a line segment, determine the index to the left of the point where the line segment crosses the spline.
	//  p0: The first point of the line segment.
	//  p1: The second point of the line segment.
	// returns: An object with the following fields:
	//  index: The index of the point to the left of the crossing point.
	//  point: The exact point of the crossing.
	getIndexOfCrossing(start, end)
	{
		// Start at the midpoint of the first segment
		let rayStart = end.add(start).scale(0.5);
		let rayDir = end.subtract(rayStart);
		rayDir = rayDir.normalize();

		const pRes = this.getClosestRayIntersection(rayStart, rayDir, undefined, 1e-3);

		let idx = pRes.index;
		let idxNext = (idx + 1) % this.getNumPoints();
		if(idxNext == 0 && !this.isClosed)
			return { index: -1, point: null };

		const intersectionPoint = rayDir.scale(pRes.distance).add(rayStart);
		return { index: idx, point: intersectionPoint };
	}

	// Given a path spline, project that path to this spline and cut everything off of spline that is on the "right" side of the path.
	// The spline to cut by is assumed to end on or near this spline on both ends.
	//   pOtherSpline: The spline to cut by.
	//   returns: a new spline that is the result of cutting this spline by pOtherSpline. or null if the spline could not be cut.
	cutByPath(pOtherSpline, optOffset = 0.0, optUp3dVector = null)
	{
		if(pOtherSpline.getNumPoints() < 2) {
			// Cutting path must have at least 2 points.
			return null;
		}

		let bIsThis3D = this.getIs3D();
		let bIsOther3D = pOtherSpline.getIs3D();

		if(bIsThis3D != bIsOther3D)
			return null;

		let spline = this.clone();
		let other = pOtherSpline.clone();

		// This only works for counterclockwise splines.
		spline.ensureOrientation(false);

		let normal;
		let projCenter;
		if(bIsThis3D) {
			// If this spline is 3D, project it to 2D
			normal = optUp3dVector || spline.getNormal();
			const normalDist = BABYLON.Vector3.Dot(spline.getCenter(), normal);
			projCenter = normal.scale(normalDist);
			spline.projectOntoPlane(normal, projCenter);

			other.offsetCurve(optOffset, normal, true);
			other.projectOntoPlane(normal, projCenter);
		}

		let p1 = other.getPoint(1).point;
		let p0 = other.getPoint(0).point;
		let crossing1Info = spline.getIndexOfCrossing(p1, p0);

		const otherNumPoints = other.getNumPoints();
		let pPenult = other.getPoint(otherNumPoints - 2).point;
		let pUlt = other.getPoint(otherNumPoints - 1).point;
		let crossing2Info = spline.getIndexOfCrossing(pPenult, pUlt);

		// Split out the data from the results of indexOfCrossing()
		let lastKeptIdx = crossing1Info.index;
		let firstKeptIdx = crossing2Info.index;
		let firstKeptIdxUsed = (crossing2Info.index + 1) % spline.getNumPoints();
		if(firstKeptIdx == -1 || lastKeptIdx == -1)
			return null;

		// Now we need to trim off the points that are on the other side of the path and concatenate the path to complete the loop.
		let newPoints = [];
		let pointMapping = [];

		if(lastKeptIdx > firstKeptIdx) {
			newPoints = spline.points.slice(firstKeptIdxUsed, lastKeptIdx + 1);
			pointMapping = Array.from({length: lastKeptIdx - firstKeptIdx}, (_, pointIndex) => pointIndex + firstKeptIdxUsed);
		}
		else {
			let sameSegmentSide = false;
			if (lastKeptIdx === firstKeptIdx) {
				// Beginning and end on the same segment
				// Determine if we need to remove or keep all the existing points depending on which side of the start the end of the path is on
				let pathDir = pUlt.subtract(p0);
				let rayDir = p0.subtract(p1);
				sameSegmentSide = BABYLON.Vector3.Cross(new BABYLON.Vector3(rayDir.x, rayDir.y, 0), new BABYLON.Vector3(pathDir.x, pathDir.y, 0)).z < 0;
			}

			if(lastKeptIdx < firstKeptIdx || sameSegmentSide) {
				// When lastKeptIdx <= firstKeptIdx, we need to check if firstKeptIdxUsed = 0 (i.e firstKeptIdx = N - 1)
				// because in that case [firstKeptIdxUsed, N) and [0, lastKeptIdx + 1) will yield repeated indexes
				if(firstKeptIdxUsed != 0) {
					newPoints = spline.points.slice(firstKeptIdxUsed, spline.getNumPoints());
					pointMapping = Array.from({length: spline.getNumPoints() - firstKeptIdxUsed}, (_, pointIndex) => pointIndex + firstKeptIdxUsed);
				}
				newPoints = newPoints.concat(spline.points.slice(0, lastKeptIdx + 1));
				pointMapping = pointMapping.concat(Array.from({length: lastKeptIdx + 1}, (_, pointIndex) => pointIndex));
				
				// orientation of the cut spline is unreliable so at least try to fix a case where points will be inverted on the result
				// this only affects a same segment cut because otherwise it should be resolved using the indexes
				if (sameSegmentSide && Utils2D.isPointOnSegment(crossing2Info.point, newPoints.at(-1).point, crossing1Info.point)) {
					other.reverse();
					const tmp = crossing1Info;
					crossing1Info = crossing2Info;
					crossing2Info = tmp;
				}
			}
		}

		newPoints.push({point: crossing1Info.point, type: other.points[0]});
		pointMapping.push(-1);
		newPoints = newPoints.concat(other.points.slice(1, other.points.length - 1));
		pointMapping = pointMapping.concat(Array.from({length: other.points.length - 2}, (_) => -1));
		newPoints.push({point: crossing2Info.point, type: other.points[other.points.length - 1]});
		pointMapping.push(firstKeptIdx);

		spline.setFromPointList(newPoints);
		if(bIsThis3D)
			spline.unprojectOntoPlane(normal, projCenter);

		return {spline: spline, pointMapping: pointMapping};
	}

    findClosestSourcePoint(point) {
        let targetPoint = point; // expected to be 3d
        if(!this.getIs3D()) {
            targetPoint = this.allocateNewPoint2D(point.x, point.y);
        }

        let index = -1;
        let foundPoint = null;
        let closestDist = Infinity;
        for(let i = 0; i < this.points.length; i++) {
            const lengthVector = this.points[i].point.subtract(targetPoint).length();
            if(lengthVector < closestDist) {
                closestDist = lengthVector;
                index = i;
            }
        }

        if(index != -1) {
            if(this.getIs3D()) {
                foundPoint = this.allocateNewPoint3D(this.points[index].x, this.points[index].y, this.points[index].z);
            }
            else {
                foundPoint = this.allocateNewPoint2D(this.points[index].x, this.points[index].y);
            }
        }

        return { index: index, point: foundPoint};
    }

    removePointAt(point) {
        const result = this.findClosestSourcePoint(point)

        if(result.point == null) {
            console.warn("No points were removed in Spline.removePointAt")
            return {success: false};
        }

        this.points.splice(result.index, 1);
        this.resetCaches();
        return {success: true, point: result.point, index: result.index};
    }

	removeDuplicatePoints(duplicateEpsilon = 1e-4)
	{
		let vecLinearPoints = this.getLinearPoints();
		let iNumPoints = vecLinearPoints.length;
		let oldPoints = this.points;
		this.points = [];
		for(let idx = 0; idx < iNumPoints; idx++)
		{
			let idx1 = idx % iNumPoints;
			let idx2 = (idx + 1) % iNumPoints;

			const extraData = Spline.getExtraData(oldPoints[idx1]);
			if(vecLinearPoints[idx1].subtract(vecLinearPoints[idx2]).length() > duplicateEpsilon)
				this.points.push({ point: vecLinearPoints[idx1], type: POINT_TYPE.LINEAR, ...extraData });
		}
		this.resetCaches();
	}

	removeSelfIntersections(funcOptReplacementPointCallback = null, fOptTolerance = undefined)
	{
		let ignoredPoints = {};

		// Test for self intersections.
		const bIsClosed = this.getIsClosed();
		let bDidHaveIntersect;
		do
		{
			bDidHaveIntersect = false;
			this.resetCaches();
			let iLength = this.points.length - 1;
			if(bIsClosed)
				iLength++;

			let vecDistances = [];
			let simplePoints = this.points.map((pointInfo) => { return pointInfo.point; });
			let fTotalDist = Spline.computeDistancesOnPiecewiseLinear(simplePoints, vecDistances, bIsClosed);

			for(let idx = 0; idx < iLength; idx++)
			{
				let iThisIdx = idx % this.points.length;
				let iNextIdx = (idx + 1) % this.points.length;

				if(ignoredPoints[this.points[iThisIdx].point] || ignoredPoints[this.points[iNextIdx].point])
					continue;

				let pRes = this.getLineSegmentIntersection(this.points[iThisIdx].point, this.points[iNextIdx].point, null, iThisIdx, fOptTolerance);

				if(pRes.distance === Number.MAX_VALUE)
					continue;

				// Otherwise, we need to cut the point between us and the other index
				let iMinIndex = Math.min(iThisIdx, pRes.index);
				let iMaxIndex = Math.max(iThisIdx, pRes.index);

				// Let's not remove sequential vertices, since we can have precision issues if there's a tiny segment intersecting
				// another one in 3D.
				if(iMaxIndex - iMinIndex <= 1)
					continue;

				let toInsert = [{ point: pRes.point, type: POINT_TYPE.LINEAR }];
				if(funcOptReplacementPointCallback)
				{
					let newPoints = funcOptReplacementPointCallback(pRes.point);
					toInsert = newPoints.map((vecPoint) => { return { point: vecPoint, type: POINT_TYPE.LINEAR }; });

					toInsert.forEach((info) => { ignoredPoints[info.point] = true; });					
				}

				// Intelligently decide which one is better - go by the longer segment right now, though
				// this may not be always the best choice.
				let fMinMaxLength = vecDistances[iMaxIndex] - vecDistances[iMinIndex];
				let fTheRest = fTotalDist - fMinMaxLength;
				if(fMinMaxLength > fTheRest)
				{
					let iCutStartIndex = (iMinIndex + 1) % this.points.length;
					this.points = [this.points[iMinIndex], ...toInsert, ...this.points.slice(iCutStartIndex, iMaxIndex + 1)];
				}
				else
					this.points.splice(iMinIndex + 1, iMaxIndex - iMinIndex, ...toInsert);

				bDidHaveIntersect = true;
				break;
			}
		} while(bDidHaveIntersect);

		this.resetCaches();
	}

	computePerPointNormals(vecLinearPoints, optUpVec3D = null, bEnsureEqualSideDistance = false)
	{
		const bIs3D = this.getIs3D();
		const bIsClosed = this.getIsClosed();

		// TODO: Option for per-side offset, not per vertex?
		let perPointNormals = [];
		let perPointDirections = Spline.computeSmoothedPerPointDirections(vecLinearPoints, bIsClosed);

		let sharpDirs = null;
		if(bEnsureEqualSideDistance)
			sharpDirs = Spline.computeSharpPerPointDirections(vecLinearPoints, bIsClosed);

		if(!bIs3D)
		{
			let vecNormal = this.allocateNewPoint2D();	
			let vecSharpNormal = this.allocateNewPoint2D();	
			for(let idx = 0; idx < perPointDirections.length; idx++)
			{
				vecNormal.x = perPointDirections[idx].y;
				vecNormal.y = -perPointDirections[idx].x;				
				if(bEnsureEqualSideDistance)
				{
					vecSharpNormal.x = sharpDirs[idx].y;
					vecSharpNormal.y = -sharpDirs[idx].x;
					const fCosine = BABYLON.Vector2.Dot(vecNormal, vecSharpNormal);
					perPointNormals.push(vecNormal.scale(1.0/fCosine));
				}
				else
					perPointNormals.push(this.allocateNewPoint2D(perPointDirections[idx].y, -perPointDirections[idx].x));
			}
			return perPointNormals;
		}

		let vecNormal = this.allocateNewPoint3D();	
		let vecSharpNormal = this.allocateNewPoint3D();	
		for(let idx = 0; idx < perPointDirections.length; idx++)
		{
			if(optUpVec3D)
			{
				Utils3D.rotatePointAroundAxisToRef(optUpVec3D, Math.PI/2.0, perPointDirections[idx], vecNormal);
				if(bEnsureEqualSideDistance)
				{
					Utils3D.rotatePointAroundAxisToRef(optUpVec3D, Math.PI/2.0, sharpDirs[idx], vecSharpNormal);
					const fAngleDeg = Utils3D.getAngleBetweenVectorsOnPlane(vecNormal, vecSharpNormal, optUpVec3D);
					const fCosine = Math.cos(fAngleDeg/180.0*Math.PI);
					vecNormal.scaleInPlace(1.0/fCosine);
				}
			}
			else
				vecNormal.set(perPointDirections[idx].y, -perPointDirections[idx].x, perPointDirections[idx].z);
			perPointNormals.push(vecNormal.clone());
		}

		return perPointNormals;
	}

	offsetCurve(fOffsetValue, optUpVec3D = null, bEnsureEqualSideDistance = false, bDisablePostProcessing = false)
	{
		if(Math.abs(fOffsetValue) < 1e-6)
			return;

		const bIs3D = this.getIs3D();

		let fNormalMult = 1.0;
		// For 3D, we need to either determine orientation based on an external
		// normal, or not flip it at all
		if(!this.getIsClockwise() && !bIs3D)
			fNormalMult = -1.0;

		// if(bIs3D)
		//	CoreUtils.warn(`Offsetting a 3D curve; please ensure consistent orientation using ensureOrientation(), otherwise, the offset direction may be unpredictable.`);

		let vecLinearPoints = this.getLinearPoints(bIs3D, false, null);
		let perPointNormals = this.computePerPointNormals(vecLinearPoints, optUpVec3D, bEnsureEqualSideDistance);

		this.points = [];
		for(let idx = 0; idx < vecLinearPoints.length; idx++)
		{			
			let vecNormal = perPointNormals[idx];
			this.points.push({ point: vecLinearPoints[idx].add(vecNormal.scale(fOffsetValue*fNormalMult)), type: POINT_TYPE.LINEAR });
		}

		this.resetCaches();

		if(!bDisablePostProcessing)
		{
			this.removeDuplicatePoints();
			this.removeSelfIntersections();
			// After removing self intersections, we can duplicates again. Remove them.
			this.removeDuplicatePoints();
			this.removeCollinearPoints();
		}
	}

	projectOntoPlane(vecPlaneNormal, vecOptCenter = null)
	{
		if(!this.getIs3D())
			return this;

		let vecCenter = vecOptCenter ? vecOptCenter : this.allocateNewPoint();

		let vecUpAxisOut = this.allocateNewPoint3D();
		let vecSideAxisOut = this.allocateNewPoint3D();
		Utils3D.getCoordSystemFromNormal(vecPlaneNormal, vecUpAxisOut, vecSideAxisOut);

		for(let pointInfo of this.points)
		{
			let vecToCenter = pointInfo.point.subtract(vecCenter);

			let fX = BABYLON.Vector3.Dot(vecToCenter, vecSideAxisOut);
			let fY = BABYLON.Vector3.Dot(vecToCenter, vecUpAxisOut);

			pointInfo.point = this.allocateNewPoint2D(fX, fY);
		}
		this.resetCaches();
		return this;
	}

	unprojectOntoPlane(vecPlaneNormal, vecOptCenter = null)
	{
		if(this.getIs3D())
			return;

		let vecCenter = vecOptCenter ? vecOptCenter : this.allocateNewPoint3D();

		let vecUpAxisOut = this.allocateNewPoint3D();
		let vecSideAxisOut = this.allocateNewPoint3D();
		Utils3D.getCoordSystemFromNormal(vecPlaneNormal, vecUpAxisOut, vecSideAxisOut);

		for(let pointInfo of this.points)
		{
			let newPoint = vecSideAxisOut.scale(pointInfo.point.x).add(vecUpAxisOut.scale(pointInfo.point.y));
			newPoint.addInPlace(vecCenter);
			pointInfo.point = newPoint;
		}
		this.resetCaches();
	}

	isPointInside(vecPoint, optPerpEpsilon = 0.00012)
	{
		let linearPoints;
		if(this.getIs3D())
		{
			let planeNormal = this.getNormal();

			// Get the perp distance:
			let toStart = vecPoint.subtract(this.points[0].point);
			if(Math.abs(BABYLON.Vector3.Dot(toStart, planeNormal)) > optPerpEpsilon)
				return false;

			const origin = this.allocateNewPoint3D();
			linearPoints = GeoUtils.projectPointsOntoPlane(this.getLinearPoints3D(), planeNormal, origin);
			vecPoint = GeoUtils.projectPointsOntoPlane([vecPoint], planeNormal, origin)[0];
		}
		else
			linearPoints = this.getLinearPoints();
		return Utils2D.isPointInPoly(linearPoints, vecPoint);
	}

	createThickPolyline(thickness, bAddPointForClosed = true, align = 'middle')
	{
		this.ensureOrientation();
		const bIs3D = this.getIs3D();
		let vecLinearPoints = this.getLinearPoints(bIs3D, bAddPointForClosed, null);

		const numPoints = vecLinearPoints.length;
		if(numPoints < 2)
			return { 
				points: [],
				distances: [],
				length: 0,
				insidePath: [],
				outsidePath: [],
			};

		let binormal;
		let angle;
        const insidePath = [];
        const outsidePath = [];
		const halfWidth = thickness/2;

		const miterFirstLast = this.isClosed && bAddPointForClosed;
        for(let i = 0; i < numPoints; i++)
		{
            const point = vecLinearPoints[i];
			
            const nextPoint = vecLinearPoints[i + 1] || (miterFirstLast ? vecLinearPoints[1] : point);
            const prevPoint = vecLinearPoints[i - 1] || (miterFirstLast ? vecLinearPoints[numPoints - 2] : point);
        
            const vecForward = nextPoint.subtract(point).normalize();
            const vecBackward = prevPoint.subtract(point).normalize();
            const direction = vecForward.subtract(vecBackward);

			if(bIs3D)
				binormal = new BABYLON.Vector3(-direction.z, 0, direction.x);
			else
				binormal = new BABYLON.Vector2(-direction.y, direction.x);
			binormal.normalize();
			
			let otherVec = point.subtract(prevPoint); 
			if(bIs3D)
				angle = BABYLON.Vector3.GetAngleBetweenVectors(binormal, otherVec, BABYLON.Vector3.Down());
			else
				angle = Utils2D.getAngleBetweenVectors(binormal, otherVec)/180*Math.PI;
            
            let fixedDistance = Math.abs(Math.sin(angle));
			if(Math.abs(fixedDistance) < 1e-6)
				fixedDistance = 1.0;

			if(align === 'outside')
			{
				outsidePath.push(point.add(binormal.scale(-thickness/fixedDistance)));
				insidePath.push(point.clone());
			}
			else if(align === 'inside')
			{
				insidePath.push(point.add(binormal.scale(thickness/fixedDistance)));
				outsidePath.push(point.clone());
			}
			else
			{
				// Middle
				insidePath.push(point.add(binormal.scale(halfWidth/fixedDistance)));
				outsidePath.push(point.add(binormal.scale(-halfWidth/fixedDistance)));
			}
        }

        insidePath.reverse();

		let vecDistancesInside = [];
		let fTotalInner = Spline.computeDistancesOnPiecewiseLinear(insidePath, vecDistancesInside, false);
		vecDistancesInside = vecDistancesInside.map((dist) => (fTotalInner - dist)/fTotalInner);
		let vecDistancesOutside = [];
		let fTotalOuter = Spline.computeDistancesOnPiecewiseLinear(outsidePath, vecDistancesOutside, false);
		vecDistancesOutside = vecDistancesOutside.map((dist) => dist/fTotalOuter);

		let fTotalLength = Spline.computeDistancesOnPiecewiseLinear(vecLinearPoints, [], false);

        return { 
			points: outsidePath.concat(insidePath),
			distances: vecDistancesOutside.concat(vecDistancesInside),
			length: fTotalLength,
			insidePath: insidePath.map((point) => { return point.clone(); }),
			outsidePath: outsidePath.map((point) => { return point.clone(); }),
		};
	}

	doesContain(point1, point2, epsilon = undefined, fPerpEpsilon = undefined)
	{
		const is3D = this.getIs3D();
		const ownLinearPoints = is3D ? this.getLinearPoints3D(true) : this.getLinearPoints(false, true);
		for(let iOwnPoint = 0; iOwnPoint < ownLinearPoints.length - 1; iOwnPoint++)
		{
			const iOwnNextPoint = iOwnPoint + 1;
			if(Utils3D.doesSegmentContain(ownLinearPoints[iOwnPoint], ownLinearPoints[iOwnNextPoint], point1, point2, epsilon, fPerpEpsilon))
				return true;
		}

		return false;
	}

	fixInvalidPoints()
	{
		const is3D = this.getIs3D();
		const ownLinearPoints = is3D ? this.getLinearPoints3D(true) : this.getLinearPoints(false, true);		
		const filteredPoints = ownLinearPoints.filter(point => point.y > -1e+200 && point.y < 1e+200);
		let yMode = CoreUtils.mode(filteredPoints.map(point => point.y), 1e-4);
		for(let iPoint = 0; iPoint < this.points.length; iPoint++)
		{
			const pointInfo = this.points[iPoint];
			if(Math.abs(pointInfo.point.y - yMode) > 1e-4)
				pointInfo.point.y = yMode;
		}
		this.resetCaches();
	}

	getIsCornerConcave(iPoint, opt3DNormal = null)
	{
		let iPrevPoint = iPoint - 1;
		if(iPrevPoint < 0)
			iPrevPoint += this.points.length;
		let iNextPoint = (iPoint + 1) % this.points.length;
		const bIsClockwise = this.getIsClockwise(opt3DNormal);
		const tempSpline = new Spline({points: [this.points[iPrevPoint], this.points[iPoint], this.points[iNextPoint]], isClosed: true});
		return tempSpline.getIsClockwise(opt3DNormal) != bIsClockwise;
	}

	interpolate(fPercValueIn)
	{
		const fPercValue = CoreUtils.clamp(fPercValueIn, 0, 1);
		let convertedPoints;
		if(this.getIs3D())
			convertedPoints = this.getLinearPoints3D();
		else
			convertedPoints = this.getLinearPoints();

		let vecDistances = [];
		let fTotalDist = Spline.computeDistancesOnPiecewiseLinear(convertedPoints, vecDistances, this.getIsClosed());

		let prevDist = 0;
		const fTargetDist = fTotalDist*fPercValue;
		for(let idx = 1; idx < vecDistances.length; idx++)
		{
			let currDist = vecDistances[idx];
			if(fTargetDist >= prevDist && fTargetDist < currDist)
			{
				const edgeInterp = (fTargetDist - prevDist)/(currDist - prevDist);
				return { startVertexIndex: idx - 1, edgeInterpValue: edgeInterp };
			}
			prevDist = currDist;
		}

		// Otherwise, return the end
		return { startVertexIndex: convertedPoints.length - 1, edgeInterpValue: 1.0 };
	}

	convertVertexAndInterpToPercent(startVertexIndex, edgeInterpValue)
	{
		let convertedPoints;
		if(this.getIs3D())
			convertedPoints = this.getLinearPoints3D();
		else
			convertedPoints = this.getLinearPoints();

		let vecDistances = [];
		let fTotalDist = Spline.computeDistancesOnPiecewiseLinear(convertedPoints, vecDistances, this.getIsClosed());

		let fInterpDist = vecDistances[startVertexIndex] + (vecDistances[startVertexIndex + 1] - vecDistances[startVertexIndex])*edgeInterpValue;
		return fInterpDist/fTotalDist;
	}
}
